// Calculate work hours and costs from route data
// calculationType: optional override from customer config ('hours_rate', 'stops_price', 'fixed_rate', 'manual_total')
export const calculateTotals = (data, calculationType = null) => {
  const kmTotal = data.km_end && data.km_start ? parseInt(data.km_end) - parseInt(data.km_start) : 0;
  
  // Parse times
  const parseTime = (timeStr) => {
    const [hours, minutes] = (timeStr || '00:00').split(':').map(Number);
    return hours + minutes / 60;
  };
  
  const startHours = parseTime(data.start_time);
  const endHours = parseTime(data.end_time);
  const breakHours = parseTime(data.break_time);
  
  let workHours = endHours - startHours - breakHours;
  // Handle overnight shifts (e.g. 23:30 -> 06:00)
  if (endHours < startHours) {
    workHours = (endHours + 24) - startHours - breakHours;
  }
  if (workHours < 0) workHours = 0;
  
  const hourlyRate = parseFloat(data.hourly_rate) || 0;
  const startCost = parseFloat(data.start_cost) || 0;
  
  // Determine calculation type: explicit param > customer config > legacy fallback
  const calcType = calculationType 
    || data.calculation_type
    || (data.customer === 'bpost' ? 'manual_total' 
      : data.customer === 'dhl' ? 'stops_price' 
      : 'hours_rate');
  
  let workCost, totalCost;
  
  switch (calcType) {
    case 'manual_total':
      workCost = 0;
      totalCost = startCost;
      break;
    case 'stops_price': {
      const stops = parseInt(data.stops) || 0;
      workCost = 0;
      totalCost = stops * startCost;
      break;
    }
    case 'fixed_rate':
    case 'hours_rate':
    default:
      workCost = workHours * hourlyRate;
      totalCost = workCost + startCost;
      break;
  }
  
  return {
    km_total: kmTotal,
    work_hours: workHours,
    work_cost: workCost,
    total_cost: totalCost
  };
};

// Calculate summary from routes array (toplamlar + gün/şoför ortalamaları için sayaçlar)
export const calculateSummary = (routes) => {
  const days = new Set();
  const drivers = new Set();
  const acc = { totalHours: 0, totalCost: 0, totalKm: 0, totalStops: 0, kmRoutes: 0, stopRoutes: 0, count: 0 };
  routes.forEach((route) => {
    const km = Number(route.km_total) || 0;
    const stops = Number(route.stops) || 0;
    acc.totalHours += route.work_hours || 0;
    acc.totalCost += route.total_cost || 0;
    acc.totalKm += km;
    acc.totalStops += stops;
    if (km > 0) acc.kmRoutes += 1;
    if (stops > 0) acc.stopRoutes += 1;
    acc.count += 1;
    if (route.date) days.add(route.date);
    const dk = route.driver_id || (route.driver_name || '').trim().toLowerCase();
    if (dk) drivers.add(dk);
  });
  return { ...acc, dayCount: days.size, driverCount: drivers.size };
};

// Format currency
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

// Format hours
export const formatHours = (hours) => {
  if (!hours) return '0:00';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};

// Get month options for filter
export const getMonthOptions = (t, lang = 'tr') => {
  const allMonthsLabel = t ? (t('routes.allMonths') || 'Tüm Aylar') : 'Tüm Aylar';
  const options = [{ value: 'all', label: allMonthsLabel }];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed (0=Jan, 1=Feb, etc.)
  
  // Determine locale based on language
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-US',
    'nl': 'nl-NL',
    'fr': 'fr-FR'
  };
  const locale = localeMap[lang] || 'tr-TR';
  
  for (let i = 0; i < 12; i++) {
    let year = currentYear;
    let month = currentMonth - i;
    
    // Handle year rollover
    while (month < 0) {
      month += 12;
      year -= 1;
    }
    
    // Format: YYYY-MM
    const value = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    // Create date for label
    const date = new Date(year, month, 1);
    const label = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    
    options.push({ value, label });
  }
  return options;
};
